import { useFormContext, useWatch } from "react-hook-form";
import { EventCreateEditFormValues } from "../EventCreateEditModal/useEventCreateEditForm";
import { useTranslation } from "react-i18next";
import { EventStepHeader } from "../EventStepHeader";
import { Flex } from "styled-system/jsx";
import { ControlledInput, ControlledTextArea } from "shared/components";
import { Radio, RadioGroup, Text } from "@ttc3k/trekker";
import { EnumEventLocationType } from "gql/generated";
import { EventKeywords } from "./EventKeywords/EventKeywords";
import { useEventCreateEditContext } from "../EventCreateEditContext/useEventCreateEditContext";
import { normalizeUrl } from "shared/utils/urlNormalizers";
import { EventVenue } from "../EventVenue";
import { useEffect } from "react";
import { EventHistory } from "./EventHistory";

export const EventDetails = () => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_DETAILS",
  });

  const { control, setValue } = useFormContext<EventCreateEditFormValues>();
  const locationType = useWatch({ control: control, name: "locationType" });
  const createdByEmail = useWatch({ control, name: "createdByEmail" });
  const createdByFirstName = useWatch({ control, name: "createdByFirstName" });
  const createdByLastName = useWatch({ control, name: "createdByLastName" });

  const {
    setIsVirtual,
    userType,
  } = useEventCreateEditContext();

  // Virtual events disable and skip step 2
  useEffect(() => {
    setIsVirtual(locationType === EnumEventLocationType.Virtual);
  }, [locationType, setIsVirtual]);

  return (
    <Flex flexDir="column" gap="400">
      <EventHistory />
      <EventStepHeader
        step={1}
        title={t("TITLE")}
        description={t("DESCRIPTION")}
      />

      {/*
      User email address ... ONLY for PUBLIC users ... OR users without an email address, first name, or last name
      This is to support external submissions where the user is not logged in and the event is created by an external user
      */}
      {(userType === "PUBLIC" ||
        !createdByEmail ||
        !createdByFirstName ||
        !createdByLastName) && (
        <Flex flexDir="column" gap="150">
          <Text visual="bodySemiBold">{t("LABELS.YOUR_INFO")}</Text>
          <Text visual="bodyRegular" color="text.mid">
            {t("LABELS.YOUR_INFO_HINT")}
          </Text>
          <ControlledInput
            name="createdByEmail"
            label={t("LABELS.USER_EMAIL_ADDRESS")}
          />
          <ControlledInput
            name="createdByFirstName"
            label={t("LABELS.USER_FIRST_NAME")}
          />
          <ControlledInput
            name="createdByLastName"
            label={t("LABELS.USER_LAST_NAME")}
          />
        </Flex>
      )}

      {/* Key Info */}
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold">{t("KEY_INFO.TITLE")}</Text>

        {/* Event Organizer */}
        <EventVenue
          allowCustom
          index={-1}
          isEventOrganizer
          label={t("LABELS.EVENT_ORGANIZER")}
          formFieldRoot="eventOrganizer"
        />

        {/* Event Name */}
        <ControlledInput name="title" label={t("LABELS.EVENT_NAME")} />

        {/* Event Description */}
        <ControlledTextArea
          name="description"
          label={t("LABELS.EVENT_DESCRIPTION")}
          rows={7}
          display="flex"
          flexDir="column"
        />
      </Flex>

      {/* Event Type */}
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold">{t("EVENT_TYPE.TITLE")}</Text>
        <RadioGroup
          value={locationType}
          onValueChange={({ value }: { value: string }) => {
            setValue(
              "locationType",
              value as EventCreateEditFormValues["locationType"],
              // Since this isn't a controlled form item, we need to manually update dirty value as well
              { shouldDirty: true },
            );
          }}
          gap={{
            _vertical: "150",
          }}
        >
          <Radio value={EnumEventLocationType.InPerson}>
            {t("EVENT_TYPE.IN_PERSON")}
            <Text visual="bodyRegular" color="text.light">
              {t("EVENT_TYPE.IN_PERSON_DESCRIPTION")}
            </Text>
          </Radio>

          <Radio value={EnumEventLocationType.Virtual}>
            {t("EVENT_TYPE.VIRTUAL")}
            <Text visual="bodyRegular" color="text.light">
              {t("EVENT_TYPE.VIRTUAL_DESCRIPTION")}
            </Text>
          </Radio>
        </RadioGroup>
      </Flex>

      {/* Contact Info */}
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold">{t("CONTACT_INFO.TITLE")}</Text>
        <Text visual="bodyRegular">{t("CONTACT_INFO.DESCRIPTION")}</Text>

        <Flex justifyContent="space-between" alignItems="flex-start" gap="150">
          <ControlledInput
            name="eventOrganizer.contact.email"
            label={t("LABELS.EMAIL")}
            type="email"
          />
          <ControlledInput
            name="eventOrganizer.contact.phone"
            label={t("LABELS.PHONE")}
          />
        </Flex>
        {locationType === EnumEventLocationType.Virtual && (
          <ControlledInput
            name="eventOrganizer.contact.links.website"
            label={t("LABELS.BUSINESS_WEBSITE")}
            type="url"
            normalizer={normalizeUrl}
          />
        )}
      </Flex>

      {/* Pricing and Tickets */}
      <Flex flexDir="column" gap="150">
        <Text visual="bodySemiBold">{t("PRICING_AND_TICKETS.TITLE")}</Text>

        {/* Event Pricing details */}
        <ControlledTextArea
          name="price"
          label={t("LABELS.PRICING_DETAILS")}
          hint={t("LABELS.PRICING_DETAILS_HINT")}
          rows={3}
          display="flex"
          flexDir="column"
        />

        {/* Ticket URL */}
        <ControlledInput
          name="ticketUrl"
          label={t("LABELS.EVENT_URL")}
          hint={t("LABELS.EVENT_URL_HINT")}
          type="url"
          normalizer={normalizeUrl}
        />
      </Flex>

      {/* Event Keywords */}
      <EventKeywords />
    </Flex>
  );
};
